import { onBeforeUnmount, onMounted, type Ref } from 'vue';
import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
import { updateEventProperty } from '@views/Event/Event.access';
import { type Event, EventStatusNames } from '@views/Event/EventTypes';

/** Saves right away. Resolves false when the save failed (an error alert has then already been shown). */
export type SaveEventProperty = (property: string, value: string | null, propertyLabel: string) => Promise<boolean>;
/** Like SaveEventProperty, but does nothing when the value equals the event's current value. Used by text inputs that save on blur. */
export type SaveEventPropertyIfChanged = (property: string, value: string | null, propertyLabel: string) => void;
/** Saves after the user has stopped editing the property for `delay` ms. The event is updated once the save succeeds. */
export type QueueEventProperty = (property: string, value: string | null, propertyLabel: string, delay?: number) => void;

const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

export function useEventPropertySaves(state: { event: Event | null; pendingSaves: number }, canWrite: Readonly<Ref<boolean>>) {
    const pendingTimers = new Map<string, { timer: number; flush: () => void }>();
    const saveChains = new Map<string, Promise<boolean>>();
    /** Bumped on every save or queued edit of a property, so an older save finishing doesn't overwrite a newer value. */
    const versions = new Map<string, number>();
    /** Saves sent or waiting in a property's chain. While any exist the event's value may be stale, so nothing counts as unchanged. */
    const inFlight = new Map<string, number>();

    const bumpVersion = (property: string) => {
        const version = (versions.get(property) ?? 0) + 1;
        versions.set(property, version);
        return version;
    };

    const eventRecord = () => state.event as unknown as Record<string, unknown>;

    // Values are sent as strings; keep the event's original type so boolean/number watchers don't receive e.g. "false" (truthy).
    function toEventValue(property: string, value: string | null): unknown {
        const current = eventRecord()[property];
        if (typeof current === 'boolean') return value === 'true';
        if (typeof current === 'number' && value !== null && value !== '') return Number(value);
        // The API returns dates as the event's wall-clock time with a 'Z' suffix, and readers parse them as UTC.
        // Pickers send naive "yyyy-MM-ddTHH:mm:ss", which would be parsed as browser-local time and appear shifted until reload.
        if (value && NAIVE_DATE_TIME.test(value)) return `${value}Z`;
        return value;
    }

    function applyToEvent(property: string, value: string | null) {
        if (property === 'EventProperties') eventRecord().EventPropertiesObject = JSON.parse(value!);
        else eventRecord()[property] = toEventValue(property, value);
    }

    function isUnchanged(property: string, value: string | null) {
        if (property === 'EventProperties') return false;
        // E.g. O→A then back to O while A is saving: the event still shows O, but the server is about to get A.
        if (inFlight.get(property)) return false;
        const current = eventRecord()[property] ?? '';
        // Dates are compared to the minute, as the pickers edit them; the API may add seconds, milliseconds or 'Z'.
        if (value && NAIVE_DATE_TIME.test(value)) return String(current).slice(0, 16) === value.slice(0, 16);
        return current === (toEventValue(property, value) ?? '');
    }

    // Saves of the same property run one at a time, so the last value sent is the last value stored.
    function persist(property: string, value: string | null, propertyLabel: string): Promise<boolean> {
        // Parity with legacy EditEventViewModel.saveProperty: without Write access nothing is sent.
        if (!canWrite.value) return Promise.resolve(false);
        const eventId = state.event!.EventId;
        const isActive = state.event!.Status === EventStatusNames.Active;
        const previous = saveChains.get(property) ?? Promise.resolve(true);

        state.pendingSaves++;
        inFlight.set(property, (inFlight.get(property) ?? 0) + 1);
        const saved = previous.then(async () => {
            try {
                await updateEventProperty(eventId, property, value, isActive);
                return true;
            } catch {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.PropertyNotUpdatedLabel, content: propertyLabel });
                return false;
            } finally {
                state.pendingSaves--;
                inFlight.set(property, inFlight.get(property)! - 1);
            }
        });
        saveChains.set(property, saved);
        return saved;
    }

    function cancelPending(property: string) {
        const pending = pendingTimers.get(property);
        if (!pending) return;
        clearTimeout(pending.timer);
        pendingTimers.delete(property);
    }

    // The event is only updated once the save succeeds, so a failed save leaves the previous value showing.
    const saveEventProperty: SaveEventProperty = async (property, value, propertyLabel) => {
        cancelPending(property);
        const version = bumpVersion(property);
        const saved = await persist(property, value, propertyLabel);
        if (saved && versions.get(property) === version) applyToEvent(property, value);
        return saved;
    };

    const saveEventPropertyIfChanged: SaveEventPropertyIfChanged = (property, value, propertyLabel) => {
        if (isUnchanged(property, value)) return;
        saveEventProperty(property, value, propertyLabel);
    };

    // The event is only updated if nothing newer was queued while saving, so a response can't overwrite newer typing.
    const queueEventProperty: QueueEventProperty = (property, value, propertyLabel, delay = 1000) => {
        cancelPending(property);
        const version = bumpVersion(property);

        const flush = async () => {
            pendingTimers.delete(property);
            // E.g. a picker stepped away and back again: nothing to save.
            if (isUnchanged(property, value)) return;
            const saved = await persist(property, value, propertyLabel);
            if (saved && versions.get(property) === version) applyToEvent(property, value);
        };
        pendingTimers.set(property, { timer: window.setTimeout(flush, delay), flush });
    };

    function flushPending() {
        for (const { timer, flush } of [...pendingTimers.values()]) {
            clearTimeout(timer);
            flush();
        }
    }

    onMounted(() => window.addEventListener('beforeunload', flushPending));
    onBeforeUnmount(() => {
        window.removeEventListener('beforeunload', flushPending);
        flushPending();
    });

    return { saveEventProperty, saveEventPropertyIfChanged, queueEventProperty };
}
